import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Loader2, AlertCircle, Globe, Lock, X, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { projectService } from '@/services/projectService'
import StatusManager from '@/components/ProjectCentral/StatusManager'
import WorkflowRules from '@/components/ProjectCentral/WorkflowRules'
import IntegrationSettings from '@/components/ProjectCentral/IntegrationSettings'
import ProjectLayout from '@/components/ProjectCentral/ProjectLayout'
import api from '@/services/api'

const PRESET_COLORS = [
  '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444',
  '#F97316', '#EAB308', '#22C55E', '#14B8A6',
  '#06B6D4', '#6366F1', '#84CC16', '#F43F5E',
]

const ProjectSettings = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'

  const [saving, setSaving] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [form, setForm] = useState(null)
  const [addMemberId, setAddMemberId] = useState('')

  const { data: projectData, isLoading, isError } = useQuery(
    ['projects', id],
    () => projectService.getProject(id),
    {
      onSuccess: (data) => {
        const p = data?.data?.project
        if (p && !form) {
          setForm({
            name: p.name || '',
            key: p.key || '',
            description: p.description || '',
            lead_id: p.lead_id ? String(p.lead_id) : '',
            color: p.color || '#3B82F6',
            visibility: p.visibility || 'public',
          })
        }
      }
    }
  )

  const { data: statusData } = useQuery(
    ['projects', id, 'statuses'],
    () => projectService.getStatuses(id)
  )

  const { data: usersData } = useQuery(
    'team-members',
    async () => { const r = await api.get('/users/team-members'); return r.data }
  )

  const { data: membersData } = useQuery(
    ['projects', id, 'members'],
    () => projectService.getMembers(id)
  )

  const project = projectData?.data?.project
  const statuses = statusData?.data?.statuses || []
  const users = usersData?.data?.users || []
  const members = membersData?.data?.members || []

  const memberUserIds = new Set(members.map(m => m.user_id))
  const availableUsers = users.filter(u => !memberUserIds.has(u.id))

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form?.name?.trim()) return toast.error('Project name is required')
    setSaving(true)
    try {
      await projectService.updateProject(id, {
        name: form.name.trim(),
        key: form.key,
        description: form.description?.trim() || null,
        lead_id: form.lead_id ? parseInt(form.lead_id) : null,
        color: form.color,
        visibility: form.visibility,
      })
      toast.success('Project updated')
      queryClient.invalidateQueries(['projects', id])
      queryClient.invalidateQueries('projects')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update project')
    } finally {
      setSaving(false)
    }
  }

  const handleArchive = async () => {
    try {
      await projectService.archiveProject(id)
      toast.success('Project archived')
      queryClient.invalidateQueries('projects')
      navigate('/projects')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to archive project')
    }
  }

  const handleAddMember = async () => {
    if (!addMemberId) return
    try {
      await projectService.addMember(id, { user_id: parseInt(addMemberId) })
      toast.success('Member added')
      setAddMemberId('')
      queryClient.invalidateQueries(['projects', id, 'members'])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add member')
    }
  }

  const handleRemoveMember = async (userId) => {
    try {
      await projectService.removeMember(id, userId)
      toast.success('Member removed')
      queryClient.invalidateQueries(['projects', id, 'members'])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove member')
    }
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-8 w-8 text-destructive mb-2" />
        <p className="font-medium">Failed to load project settings</p>
        <p className="text-muted-foreground text-sm mt-1">Something went wrong. Please try again.</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate(`/projects/${id}/board`)}>
          Back to Board
        </Button>
      </div>
    )
  }

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <ProjectLayout>
    <div className="max-w-2xl space-y-8">
      {/* Project Details */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold">Details</h2>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              disabled={!isAdmin}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="key">Project Key</Label>
            <Input
              id="key"
              value={form.key}
              onChange={(e) => setForm(f => ({ ...f, key: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5) }))}
              disabled={!isAdmin}
              maxLength={5}
              className="uppercase w-32 font-mono"
            />
            <p className="text-xs text-muted-foreground">2-5 uppercase letters. Used in task IDs (e.g. {form.key || 'XX'}-1)</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              disabled={!isAdmin}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Lead</Label>
            <Select
              value={form.lead_id}
              onValueChange={(v) => setForm(f => ({ ...f, lead_id: v === 'none' ? '' : v }))}
              disabled={!isAdmin}
            >
              <SelectTrigger>
                <SelectValue placeholder="No lead assigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No lead</SelectItem>
                {users.map((u) => {
                  const name = u.employee ? `${u.employee.first_name} ${u.employee.last_name}` : u.email
                  return <SelectItem key={u.id} value={String(u.id)}>{name}</SelectItem>
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Visibility</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => isAdmin && setForm(f => ({ ...f, visibility: 'public' }))}
                disabled={!isAdmin}
                className={cn(
                  "flex items-center gap-2 flex-1 rounded-md border px-3 py-2 text-sm transition-colors disabled:opacity-50",
                  form.visibility === 'public'
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-input text-muted-foreground hover:bg-muted"
                )}
              >
                <Globe className="h-4 w-4" />
                Public
              </button>
              <button
                type="button"
                onClick={() => isAdmin && setForm(f => ({ ...f, visibility: 'private' }))}
                disabled={!isAdmin}
                className={cn(
                  "flex items-center gap-2 flex-1 rounded-md border px-3 py-2 text-sm transition-colors disabled:opacity-50",
                  form.visibility === 'private'
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-input text-muted-foreground hover:bg-muted"
                )}
              >
                <Lock className="h-4 w-4" />
                Private
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {form.visibility === 'public'
                ? 'Visible to all team members'
                : 'Only invited members can access'}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => isAdmin && setForm(f => ({ ...f, color: c }))}
                  disabled={!isAdmin}
                  className="w-7 h-7 rounded-full border-2 transition-all disabled:opacity-50"
                  style={{
                    backgroundColor: c,
                    borderColor: form.color === c ? '#000' : 'transparent',
                    transform: form.color === c ? 'scale(1.15)' : 'scale(1)',
                  }}
                />
              ))}
            </div>
          </div>

          {isAdmin && (
            <Button type="submit" disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
            </Button>
          )}
        </form>
      </section>

      <Separator />

      {/* Members Management */}
      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold">Members</h2>
          <p className="text-sm text-muted-foreground">
            {form.visibility === 'private'
              ? 'Only these members can access this project.'
              : 'Members of this project. Visibility is public, so all users can access it.'}
          </p>
        </div>

        {/* Add member */}
        {isAdmin && (
          <div className="flex gap-2">
            <Select value={addMemberId} onValueChange={setAddMemberId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select a user to add..." />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.length === 0 ? (
                  <SelectItem value="_empty" disabled>All users are already members</SelectItem>
                ) : (
                  availableUsers.map((u) => {
                    const name = u.employee
                      ? `${u.employee.first_name} ${u.employee.last_name}`
                      : u.email
                    return <SelectItem key={u.id} value={String(u.id)}>{name}</SelectItem>
                  })
                )}
              </SelectContent>
            </Select>
            <Button type="button" onClick={handleAddMember} disabled={!addMemberId} size="sm" className="h-10">
              <UserPlus className="h-4 w-4 mr-1.5" />
              Add
            </Button>
          </div>
        )}

        {/* Members list */}
        <div className="space-y-1">
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No members added yet.</p>
          ) : (
            members.map((member) => {
              const name = member.user?.employee
                ? `${member.user.employee.first_name} ${member.user.employee.last_name}`
                : member.user?.email || 'Unknown'
              const initial = name.charAt(0).toUpperCase()
              const isLead = project?.lead_id === member.user_id
              const isCreator = project?.created_by === member.user_id

              return (
                <div key={member.id} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">{initial}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{name}</span>
                        {isLead && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Lead</Badge>}
                        {isCreator && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Creator</Badge>}
                      </div>
                      <span className="text-xs text-muted-foreground">{member.user?.email}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveMember(member.user_id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )
            })
          )}
        </div>
      </section>

      <Separator />

      {/* Status Management */}
      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold">Statuses</h2>
          <p className="text-sm text-muted-foreground">Drag to reorder. Status changes are saved immediately.</p>
        </div>
        <StatusManager projectId={id} initialStatuses={statuses} />
      </section>

      {/* Workflow rules */}
      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold">Workflow rules</h2>
          <p className="text-sm text-muted-foreground">Optionally limit which status a task can move to from each status.</p>
        </div>
        <WorkflowRules projectId={id} statuses={statuses} canEdit={isAdmin || (!!project?.lead_id && project.lead_id === user?.id)} />
      </section>

      {/* Integration */}
      {isAdmin && (
        <>
          <Separator />
          <section className="space-y-4">
            <div>
              <h2 className="text-base font-semibold">Integration</h2>
              <p className="text-sm text-muted-foreground">
                Connect this project to an external tool for two-way ticket sync.
              </p>
            </div>
            <IntegrationSettings projectId={id} />
          </section>
        </>
      )}

      {/* Danger Zone */}
      {isAdmin && project?.status === 'active' && (
        <>
          <Separator />
          <section className="space-y-3">
            <h2 className="text-base font-semibold text-destructive">Danger Zone</h2>
            <div className="border border-destructive/30 rounded-lg p-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Archive this project</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  The project will be hidden from the active list. Tasks are preserved.
                </p>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setArchiveOpen(true)}>
                Archive
              </Button>
            </div>
          </section>
        </>
      )}

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive "{project?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The project will be archived. You can view it by switching to the Archived filter on the Projects page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Archive Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </ProjectLayout>
  )
}

export default ProjectSettings
